/**
 * Which clicks count: a popover on the Key clicks row (and a step of
 * "+ Add goal"), so the data stays in view. Presets (Key clicks, Every click,
 * Custom) with the match rule as a tooltip, a searchable checklist of clicked
 * elements with per-version visitors (editing a preset's list switches to
 * Custom pre-filled with its matches), and a live preview of the set.
 */

import { useMemo, useState, type ReactNode } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { Search01Icon } from "@hugeicons/core-free-icons"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { formatNumeric } from "@/lib/utils/stringUtils"
import { formatChance, formatRange } from "@/lib/experiments/bayes"
import {
  CLICK_PRESETS,
  CLICK_SET_MAX_STRING,
  CLICK_SET_MAX_TARGETS,
  clickSetIncludes,
  formatChange,
  formatGoalRate,
  presetToCustom,
  type ClickElement,
  type ClickPreset,
  type ClickSet,
  type ClickSetTarget,
  type GoalComparison,
  type GoalMetricResult,
} from "@/lib/experiments/goalMetric"

export type GoalPreview = { result: GoalMetricResult; comparison: GoalComparison; loading: boolean }

const PRESET_ORDER: ClickPreset[] = ["key", "all", "custom"]

export function clickSetIsValid(set: ClickSet): boolean {
  if (set.preset !== "custom") return true
  return (
    set.targets.length > 0 &&
    set.targets.length <= CLICK_SET_MAX_TARGETS &&
    set.targets.every((t) => t.target.length <= CLICK_SET_MAX_STRING && (t.secondary ?? "").length <= CLICK_SET_MAX_STRING)
  )
}

type PanelProps = {
  draft: ClickSet
  onDraftChange: (draft: ClickSet) => void
  elements: ClickElement[]
  versions: Array<{ letter: string; label: string }>
  nameOf: (t: ClickSetTarget) => string
  preview: GoalPreview | null
  /** Use this set as the goal (select its row; the model saves it when it can). */
  onUse: () => void
  onCancel: () => void
  saving?: boolean
}

type Props = PanelProps & {
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger: ReactNode
  /** Why the editor can't open (today's backend), as a tooltip. */
  disabledReason?: string | null
  /** Tooltip for the enabled trigger. It wraps the popover trigger, never the other way round: a
   * PopoverTrigger around a component that drops its ref and props leaves the popover unanchored and hidden. */
  hint?: string
}

/** The click set editor in its own popover, opened from `trigger` (the Key clicks pencil). */
export function ClickSetEditor({ open, onOpenChange, trigger, disabledReason, hint, ...panel }: Props) {
  if (disabledReason) {
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} className="w-fit" onClick={(e) => e.stopPropagation()}>
              {trigger}
            </span>
          </TooltipTrigger>
          <TooltipContent>{disabledReason}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }
  const popoverTrigger = <PopoverTrigger asChild>{trigger}</PopoverTrigger>
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {hint ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>{popoverTrigger}</TooltipTrigger>
            <TooltipContent className="max-w-[280px]">{hint}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        popoverTrigger
      )}
      <PopoverContent
        align="start"
        sideOffset={6}
        // Don't land focus on a preset chip: it would pop its tooltip on open.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
        className="w-[460px] max-w-[calc(100vw-2rem)] p-4"
      >
        <ClickSetPanel {...panel} />
      </PopoverContent>
    </Popover>
  )
}

/** Presets, the element list, the live preview and Use: the editor's body, also used by "+ Add goal". */
export function ClickSetPanel({ draft, onDraftChange, elements, versions, nameOf, preview, onUse, onCancel, saving = false }: PanelProps) {
  const [search, setSearch] = useState("")

  const matchCount = useMemo(() => {
    const out = {} as Record<ClickPreset, number>
    for (const p of PRESET_ORDER) {
      out[p] = p === "custom" ? draft.targets.length : elements.filter((e) => clickSetIncludes({ preset: p, targets: [] }, e)).length
    }
    return out
  }, [elements, draft.targets.length])

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = q ? elements.filter((e) => `${nameOf(e)} ${e.target} ${e.secondary ?? ""}`.toLowerCase().includes(q)) : elements
    // Ticked first, so a preset's matches read at a glance.
    return [...list].sort((a, b) => Number(clickSetIncludes(draft, b)) - Number(clickSetIncludes(draft, a)))
  }, [elements, search, nameOf, draft])

  const setPreset = (preset: ClickPreset) =>
    onDraftChange(preset === "custom" ? presetToCustom(draft, elements) : { preset, targets: [] })
  // Editing a preset's list switches to Custom, pre-filled with its matches.
  const toggle = (el: ClickElement) => {
    const base = draft.preset === "custom" ? draft : presetToCustom(draft, elements)
    const has = clickSetIncludes(base, el)
    // An entry with a null secondary covers every secondary of that target.
    const covers = (t: ClickSetTarget) => t.target === el.target && (t.secondary == null || t.secondary === (el.secondary ?? null))
    onDraftChange({
      preset: "custom",
      targets: has ? base.targets.filter((t) => !covers(t)) : [...base.targets, { target: el.target, secondary: el.secondary }],
    })
  }

  const valid = clickSetIsValid(draft)
  const focus = preview?.comparison.focus
  const control = preview?.comparison.control

  return (
    <div className="flex flex-col gap-3">
        <TooltipProvider delayDuration={150}>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Which clicks count">
            {PRESET_ORDER.map((p) => (
              <Tooltip key={p}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={draft.preset === p}
                    onClick={() => setPreset(p)}
                    className={cn(
                      "inline-flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition-colors",
                      draft.preset === p
                        ? "border-ring bg-muted text-foreground"
                        : "border-border text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                    )}
                  >
                    {CLICK_PRESETS[p].label}
                    <span className="tabular-nums text-muted-foreground">{matchCount[p]}</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[260px]">{CLICK_PRESETS[p].rule}</TooltipContent>
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>

        <div className="relative">
          <HugeiconsIcon icon={Search01Icon} size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search elements" aria-label="Search elements" className="h-8 pl-8 text-xs" />
        </div>
        <ul className="max-h-[240px] overflow-y-auto rounded-lg border border-border">
          {shown.length === 0 && (
            <li className="px-3 py-6 text-center text-xs text-muted-foreground">
              {elements.length === 0 ? "No clicks recorded yet" : `No elements match “${search}”`}
            </li>
          )}
          {shown.map((el) => (
            <li key={el.key} className="border-b border-border/60 last:border-b-0">
              <label className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-muted/30">
                <Checkbox checked={clickSetIncludes(draft, el)} onCheckedChange={() => toggle(el)} className="mt-0.5" aria-label={nameOf(el)} />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-[13px] font-medium leading-4 text-foreground">{nameOf(el)}</span>
                  {el.secondary && el.secondary !== nameOf(el) && (
                    <span className="truncate text-xs leading-4 text-muted-foreground">{el.secondary}</span>
                  )}
                </span>
                <span className="shrink-0 text-right text-xs leading-4 tabular-nums text-muted-foreground">
                  {versions.map((v) => `${v.letter} ${formatNumeric(el.byVersion[v.letter] ?? 0)}`).join(" · ")}
                  <span className="block text-[10px]">{el.measure === "visitors" ? "unique visitors" : "clicks"}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-1 rounded-lg bg-muted/40 px-3 py-2.5" aria-live="polite">
          {!valid ? (
            <span className="text-xs text-muted-foreground">
              {draft.targets.length > CLICK_SET_MAX_TARGETS ? `Tick at most ${CLICK_SET_MAX_TARGETS} elements.` : "Tick at least one element."}
            </span>
          ) : !preview || preview.loading || !focus || !control ? (
            <Skeleton className="h-[13px] w-56" />
          ) : !preview.result.available ? (
            <span className="text-xs text-muted-foreground">{preview.result.reason}</span>
          ) : (
            <>
              <span className="text-[13px] leading-[18px] text-foreground tabular-nums">
                {control.label} {formatGoalRate(control.rate, preview.result.unit)} → {focus.label}{" "}
                {formatGoalRate(focus.rate, preview.result.unit)} · {formatChange(focus.change, focus.isNew)}
              </span>
              <span className="text-xs leading-4 text-muted-foreground tabular-nums">
                {focus.best != null && `${formatChance(focus.best)} confidence`}
                {focus.range && ` · likely ${formatRange(focus.range)}`}
              </span>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onCancel} className="h-8 px-3 text-xs">
            Cancel
          </Button>
          <Button onClick={onUse} disabled={!valid || saving} className="h-8 px-3 text-xs">
            Use these clicks
          </Button>
        </div>
    </div>
  )
}
